<template>
  <div class="online-users-dropdown" ref="dropdownRef">
    <button
      class="online-users-button"
      @click="toggleDropdown"
      :aria-expanded="isOpen"
      aria-haspopup="true"
      :title="`${onlineCount} Benutzer online`"
    >
      <span class="online-indicator" :class="{ 'has-users': onlineCount > 0 }"></span>
      <svg class="online-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
        <circle cx="9" cy="7" r="4"></circle>
        <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
        <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
      </svg>
      <span class="online-count">{{ onlineCount }}</span>
    </button>

    <transition name="dropdown">
      <div v-if="isOpen" class="online-users-panel">
        <div class="panel-header">
          <span class="panel-title">Online Benutzer</span>
          <span class="panel-count">{{ onlineCount }}</span>
        </div>

        <div v-if="isLoading" class="panel-loading">
          <span class="spinner"></span>
          <span>Laden...</span>
        </div>

        <div v-else-if="onlineUsers.length === 0" class="panel-empty">
          Keine Benutzer online
        </div>

        <div v-else class="panel-list">
          <div
            v-for="user in onlineUsers"
            :key="user.user_id"
            class="online-user-item"
          >
            <div class="user-avatar-small">
              <span>{{ getInitials(user.display_name || user.email) }}</span>
              <span class="status-dot"></span>
            </div>
            <div class="user-details">
              <div class="user-name-row">
                <span class="user-name">{{ user.display_name || user.email }}</span>
                <span v-if="user.is_admin" class="admin-badge">Admin</span>
              </div>
              <div class="user-location">
                <svg v-if="user.current_tool_name" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                  <circle cx="9" cy="9" r="2"></circle>
                </svg>
                <span v-if="user.current_tool_name">{{ user.current_tool_name }}</span>
                <span v-else-if="user.current_route === '/' || user.current_route === ''">Dashboard</span>
                <span v-else-if="user.current_route && user.current_route.startsWith('/admin')">Administration</span>
                <span v-else>Plattform</span>
              </div>
            </div>
            <div class="heartbeat-ago" :title="user.last_heartbeat">
              {{ formatAgo(user.seconds_since_heartbeat) }}
            </div>
          </div>
        </div>

        <div class="panel-footer">
          <span class="refresh-hint">Aktualisiert alle 30s</span>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import apiClient from '@/services/api'

interface OnlineUser {
  user_id: number
  display_name: string | null
  email: string
  is_admin: boolean
  current_route: string | null
  current_tool_id: number | null
  current_tool_name: string | null
  current_tool_path: string | null
  last_heartbeat: string
  seconds_since_heartbeat: number
}

const isOpen = ref(false)
const isLoading = ref(false)
const onlineUsers = ref<OnlineUser[]>([])
const onlineCount = ref(0)
const dropdownRef = ref<HTMLElement | null>(null)
let pollInterval: ReturnType<typeof setInterval> | null = null

function toggleDropdown() {
  isOpen.value = !isOpen.value
  if (isOpen.value) {
    fetchOnlineUsers()
  }
}

async function fetchOnlineUsers() {
  try {
    isLoading.value = onlineUsers.value.length === 0
    const response = await apiClient.get('/v1/admin/online-users')
    if (response?.data) {
      onlineUsers.value = response.data.online_users ?? []
      onlineCount.value = response.data.count ?? 0
    }
  } catch (error) {
    console.warn('[OnlineUsers] Fehler beim Laden:', error)
  } finally {
    isLoading.value = false
  }
}

function getInitials(name: string): string {
  if (!name) return '?'
  const parts = name.trim().split(' ')
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase()
  }
  return name.substring(0, 2).toUpperCase()
}

function formatAgo(seconds: number): string {
  if (seconds < 60) return 'jetzt'
  const mins = Math.floor(seconds / 60)
  return `${mins}m`
}

function handleClickOutside(event: MouseEvent) {
  if (dropdownRef.value && !dropdownRef.value.contains(event.target as Node)) {
    isOpen.value = false
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
  // Initial load for count badge
  fetchOnlineUsers()
  // Poll every 30 seconds
  pollInterval = setInterval(fetchOnlineUsers, 30_000)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
  if (pollInterval) {
    clearInterval(pollInterval)
  }
})
</script>

<style scoped>
.online-users-dropdown {
  position: relative;
}

.online-users-button {
  display: flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  padding: 6px 10px;
  border-radius: var(--border-radius-md, 6px);
  transition: background-color 0.2s;
  font-size: var(--font-size-sm, 0.875rem);
}

.online-users-button:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.online-indicator {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: var(--color-text-muted, #999);
  transition: background-color 0.3s;
}

.online-indicator.has-users {
  background-color: #22c55e;
  box-shadow: 0 0 6px rgba(34, 197, 94, 0.6);
}

.online-icon {
  flex-shrink: 0;
}

.online-count {
  font-weight: 600;
  min-width: 16px;
  text-align: center;
}

/* Panel */
.online-users-panel {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  width: 320px;
  max-height: 420px;
  background-color: var(--color-background, #fff);
  border-radius: var(--border-radius-md, 6px);
  box-shadow: var(--box-shadow-lg, 0 8px 24px rgba(0,0,0,0.15));
  overflow: hidden;
  z-index: 200;
  display: flex;
  flex-direction: column;
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background-color: var(--color-background-light, #f5f5f5);
  border-bottom: 1px solid var(--color-border, #e0e0e0);
}

.panel-title {
  font-weight: 600;
  font-size: var(--font-size-sm, 0.875rem);
  color: var(--color-text, #333);
}

.panel-count {
  background-color: var(--color-primary, #2563eb);
  color: white;
  font-size: 0.75rem;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 12px;
  min-width: 24px;
  text-align: center;
}

.panel-loading,
.panel-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 24px 16px;
  color: var(--color-text-light, #888);
  font-size: var(--font-size-sm, 0.875rem);
}

.spinner {
  width: 16px;
  height: 16px;
  border: 2px solid var(--color-border, #e0e0e0);
  border-top-color: var(--color-primary, #2563eb);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* User list */
.panel-list {
  overflow-y: auto;
  max-height: 320px;
}

.online-user-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  transition: background-color 0.15s;
  border-bottom: 1px solid var(--color-border-light, #f0f0f0);
}

.online-user-item:hover {
  background-color: var(--color-background-light, #f9f9f9);
}

.online-user-item:last-child {
  border-bottom: none;
}

.user-avatar-small {
  position: relative;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: var(--color-primary, #2563eb);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  font-weight: 600;
  flex-shrink: 0;
}

.status-dot {
  position: absolute;
  bottom: -1px;
  right: -1px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: #22c55e;
  border: 2px solid var(--color-background, #fff);
}

.user-details {
  flex: 1;
  min-width: 0;
}

.user-name-row {
  display: flex;
  align-items: center;
  gap: 6px;
}

.user-name {
  font-size: var(--font-size-sm, 0.875rem);
  font-weight: 500;
  color: var(--color-text, #333);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.admin-badge {
  font-size: 0.65rem;
  font-weight: 700;
  color: var(--color-primary, #2563eb);
  background-color: var(--color-primary-light, #dbeafe);
  padding: 1px 6px;
  border-radius: 4px;
  text-transform: uppercase;
  flex-shrink: 0;
}

.user-location {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.75rem;
  color: var(--color-text-light, #888);
  margin-top: 2px;
}

.user-location svg {
  flex-shrink: 0;
  opacity: 0.6;
}

.heartbeat-ago {
  font-size: 0.7rem;
  color: var(--color-text-muted, #aaa);
  flex-shrink: 0;
}

/* Footer */
.panel-footer {
  padding: 8px 16px;
  border-top: 1px solid var(--color-border, #e0e0e0);
  background-color: var(--color-background-light, #f5f5f5);
}

.refresh-hint {
  font-size: 0.7rem;
  color: var(--color-text-muted, #aaa);
}

/* Dropdown animation */
.dropdown-enter-active,
.dropdown-leave-active {
  transition: opacity 0.2s, transform 0.2s;
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

/* Dark mode */
:global(.mode-dark) .online-users-panel {
  background-color: var(--color-background-dark, #1d1d1b);
  border: 1px solid var(--color-border, #444);
}

:global(.mode-dark) .panel-header {
  background-color: var(--color-background-darker, #161614);
  border-bottom-color: var(--color-border, #444);
}

:global(.mode-dark) .online-user-item:hover {
  background-color: rgba(255, 255, 255, 0.05);
}

:global(.mode-dark) .panel-footer {
  background-color: var(--color-background-darker, #161614);
  border-top-color: var(--color-border, #444);
}

:global(.mode-dark) .status-dot {
  border-color: var(--color-background-dark, #1d1d1b);
}

:global(.mode-dark) .admin-badge {
  background-color: rgba(37, 99, 235, 0.2);
}

@media (max-width: 768px) {
  .online-users-panel {
    width: 280px;
    right: -40px;
  }

  .online-count {
    display: none;
  }
}
</style>
